import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { forexApi, type ForexAuditLog } from "@/lib/forex-api";

interface AuditRecord {
  id: string;
  time: string;
  operator: string;
  action: string;
  target: string;
  detail: string;
  ip: string;
  requestId: string;
  riskLevel: string;
  result: "success" | "failure" | "unknown";
}

function formatAuditTime(value: number): string {
  return new Date(value * 1000).toLocaleString("zh-CN", { hour12: false });
}

function mapAuditLog(item: ForexAuditLog): AuditRecord {
  const result = item.result === "success" || item.result === "failure" ? item.result : "unknown";
  return {
    id: item.audit_id,
    time: formatAuditTime(item.created_at),
    operator: item.operator || "system",
    action: item.action,
    target: item.target_id,
    detail: JSON.stringify(item.detail),
    ip: item.source_ip || "未记录",
    requestId: item.request_id || "未记录",
    riskLevel: item.risk_level || "unknown",
    result,
  };
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildAuditCsv(records: AuditRecord[]): string {
  const header = ["时间", "操作人", "动作", "目标", "详情", "请求 ID", "IP", "风险级别", "结果"];
  const rows = records.map((record) => [
    record.time,
    record.operator,
    record.action,
    record.target,
    record.detail,
    record.requestId,
    record.ip,
    record.riskLevel,
    record.result === "success" ? "成功" : record.result === "failure" ? "失败" : "未记录",
  ]);

  return [header, ...rows]
    .map((row) => row.map((cell) => escapeCsvCell(cell)).join(","))
    .join("\n");
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function AuditTab() {
  const [auditLogs, setAuditLogs] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [remoteReady, setRemoteReady] = useState(false);
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("target") || params.get("request_id") || "");
  const [revision, setRevision] = useState(0);
  const [resultFilter, setResultFilter] = useState("");
  const [exportState, setExportState] = useState<"idle" | "done" | "empty" | "error">("idle");
  const [exportMessage, setExportMessage] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    forexApi.listAuditLogs()
      .then((response) => {
        if (!active) return;
        setAuditLogs(response.items.map(mapAuditLog));
        setRemoteReady(true);
      })
      .catch(() => {
        if (!active) return;
        setAuditLogs([]);
        setRemoteReady(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision]);

  const filtered = auditLogs.filter((a) => {
    if (resultFilter && a.result !== resultFilter) return false;
    if (search && !a.operator.includes(search) && !a.action.includes(search) && !a.target.includes(search) && !a.requestId.includes(search)) return false;
    return true;
  });

  const handleExport = () => {
    if (filtered.length === 0) {
      setExportState("empty");
      setExportMessage("当前筛选条件下没有可导出的真实审计记录");
      return;
    }

    try {
      downloadCsv(`forex-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`, buildAuditCsv(filtered));
      setExportState("done");
      setExportMessage(`已基于当前接口数据导出 ${filtered.length} 条 CSV 记录`);
    } catch {
      setExportState("error");
      setExportMessage("CSV 文件生成失败，请重试或检查浏览器下载权限");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 text-sm"><p>以下为当前接口已读取的后台审计；开通与实例记录以原任务历史为准。</p><button disabled={loading} className="text-brand" onClick={() => setRevision(value => value + 1)}>刷新记录</button></div>
      <div>
        <h2 className="text-lg font-semibold text-text-primary">操作审计</h2>
        <p className="text-xs text-text-muted mt-0.5">{loading ? "正在同步 /quantum/business/forex/audit-logs..." : remoteReady ? "已接入 /quantum/business/forex/audit-logs" : "审计日志接口不可用，请检查服务端状态"}</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜索操作人/动作/目标..." className="h-9 w-[260px] pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none" />
        </div>
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部结果</option><option value="success">成功</option><option value="failure">失败</option><option value="unknown">未记录</option>
        </select>
        <button
          onClick={handleExport}
          disabled={loading || !remoteReady}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ml-auto whitespace-nowrap cursor-pointer ${
            exportState === "done" ? "bg-emerald-500 text-white" :
            exportState === "empty" || exportState === "error" ? "bg-amber-500 text-white" :
            "bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand"
          }`}
        >
          {exportState === "done" ? <><i className="ri-check-line text-sm" />已导出</> :
           exportState === "empty" ? <><i className="ri-information-line text-sm" />无记录</> :
           exportState === "error" ? <><i className="ri-error-warning-line text-sm" />导出失败</> :
           <><i className="ri-download-line text-sm" />导出审计日志</>}
        </button>
      </div>
      {exportMessage && (
        <div className={`rounded-lg border px-3 py-2 text-xs ${
          exportState === "done"
            ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600"
            : "border-amber-500/20 bg-amber-500/5 text-amber-600"
        }`}>
          {exportMessage}
        </div>
      )}
      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">时间</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作人</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">动作</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">目标</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">详情</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">IP</th>
                <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">结果</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className={`border-b border-surface-border hover:bg-surface/30 transition-colors ${a.result === "failure" ? "bg-red-500/[0.02]" : ""}`}>
                  <td className="px-4 py-2.5 text-text-muted font-mono text-[11px] whitespace-nowrap">{a.time}</td>
                  <td className="px-4 py-2.5 text-text-primary text-xs font-medium whitespace-nowrap">{a.operator}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${a.result === "failure" ? "bg-red-500/10 text-red-500" : "bg-brand/10 text-brand"}`}>{a.action}</span>
                  </td>
                  <td className="px-4 py-2.5 text-text-primary text-xs">{a.target}</td>
                  <td className="px-4 py-2.5 text-text-secondary text-[11px] max-w-[300px]">
                    <p className="truncate" title={a.detail}>{a.detail}</p>
                    <p className="mt-0.5 truncate font-mono text-[9px] text-text-muted" title={a.requestId}>请求 {a.requestId} · 风险 {a.riskLevel}</p>
                  </td>
                  <td className="px-4 py-2.5 text-text-muted font-mono text-[10px]">{a.ip}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      a.result === "success" ? "bg-emerald-500/10 text-emerald-500" :
                      a.result === "failure" ? "bg-red-500/10 text-red-500" :
                      "bg-gray-500/10 text-gray-500"
                    }`}>
                      <i className={`text-[8px] ${a.result === "success" ? "ri-checkbox-circle-fill" : a.result === "failure" ? "ri-close-circle-fill" : "ri-question-fill"}`} />
                      {a.result === "success" ? "成功" : a.result === "failure" ? "失败" : "未记录"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
